import { MrbBaseViewStore } from "mrb/core";
import { observable, makeObservable, action, computed } from "mobx";
import { isNil } from "lodash";
import { BulkSelect } from "application/categories/models";
import { podcastType } from "common/constants";

class CategoryBulkSelectViewStore extends MrbBaseViewStore {
    @observable isUpdating = false;
    @observable bulkSelection = new BulkSelect();

    @computed get isSelectedItemsAny() {
        return !isNil(this.bulkSelection.isAllSelected) || this.bulkSelection.selectedIds.length > 0;
    }
    constructor(rootStore, contentViewStore) {
        super(rootStore);
        makeObservable(this);
        this.contentViewStore = contentViewStore;
    }

    @action.bound
    selectAll() {
        this.bulkSelection.selectAll();
    }

    @action.bound
    deselectAll() {
        this.bulkSelection.deselectAll();
    }

    @action.bound
    selectItem(item) {
        if (this.isUpdating) {
            return;
        }
        if (!isNil(item.podcastId)) {
            const contentAccessResult = this.rootStore.contentAccessValidator.canAccessPodcast({
                isPremium: item.isPremium,
                unavailableInMobileApp: item.isVisibleInApp === false,
                unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
            });

            if (contentAccessResult.canAccess) {
                if (item.podcastType === podcastType.podcast) {
                    this.bulkSelection.addSelectedPodcastItem(item.podcastId);
                } else {
                    this.bulkSelection.addSelectedPodcastGroupItem(item.podcastId);
                }
            }
        } else if (!isNil(item.stationId)) {
            const contentAccessResult = this.rootStore.contentAccessValidator.canAccessStation({
                isPremium: item.isPremium,
                unavailableInMobileApp: item.isVisibleInApp === false,
                unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
            });

            if (contentAccessResult.canAccess) {
                this.bulkSelection.addSelectedStationItem(item.stationId);
            }
        }
    }

    @action.bound
    deselectItem(item) {
        if (this.isUpdating) {
            return;
        }

        if (!isNil(item.podcastId)) {
            const contentAccessResult = this.rootStore.contentAccessValidator.canAccessPodcast({
                isPremium: item.isPremium,
                unavailableInMobileApp: item.isVisibleInApp === false,
                unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
            });

            if (contentAccessResult.canAccess) {
                if (item.podcastType === podcastType.podcast) {
                    this.bulkSelection.addDeselectedPodcastItem(item.podcastId);
                } else {
                    this.bulkSelection.addDeselectedPodcastGroupItem(item.podcastId);
                }
            }
        } else if (!isNil(item.stationId)) {
            const contentAccessResult = this.rootStore.contentAccessValidator.canAccessStation({
                isPremium: item.isPremium,
                unavailableInMobileApp: item.isVisibleInApp === false,
                unavailableInNakiTouch: item.isVisibleInEmbeddedApp === false,
            });

            if (contentAccessResult.canAccess) {
                this.bulkSelection.addDeselectedStationItem(item.stationId);
            }
        }
    }

    @action.bound
    async saveSelection() {
        if (!this.isUpdating) {
            this.setIsUpdating(true);
            try {
                this.rootStore.mainLoaderStore.suspend();
                const options = {
                    contentType: this.contentViewStore.contentType,
                    isAllSelected: this.bulkSelection.isAllSelected,
                    selectedPodcastIds: this.bulkSelection.selectedPodcastIds,
                    selectedStationIds: this.bulkSelection.selectedStationIds,
                    selectedPodcastGroupIds: this.bulkSelection.selectedPodcastGroupIds,
                    deselectedPodcastIds: this.bulkSelection.deselectedPodcastIds,
                    deselectedStationIds: this.bulkSelection.deselectedStationIds,
                    deselectedPodcastGroupIds: this.bulkSelection.deselectedPodcastGroupIds,
                };
                await this.rootStore.userContentManagerStore.updateContentStatusBulk(options);
                this.contentViewStore.onBulkEditSuccess(this.bulkSelection);

                return true;
            } catch (err) {
                this.rootStore.notificationStore.error(
                    "Unexpected error occurred. Please contact the support team.",
                    err
                );

                return false;
            } finally {
                this.setIsUpdating(false);
                this.rootStore.mainLoaderStore.resume();
            }
        }
    }

    @action.bound
    setIsUpdating(isUpdating) {
        this.isUpdating = isUpdating;
    }

    @action.bound
    clearSelection() {
        this.bulkSelection = new BulkSelect();
    }
}

export default CategoryBulkSelectViewStore;
